import React, { useEffect, useState, useContext, useRef } from 'react';
import { salesService } from '../../services/sales.service';
import CurrencyFormat from 'react-currency-format';
import LogicContext from '../../Context/LogicContext';
import { GetEntryData, SetEntryData, GetEditSalesOrder, ResetEditSalesOrder } from '../../store/salesOrderAtom';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GeneralModal from '../../helpers/GeneralModal';
import { createModal } from 'react-modal-promise';
import SalesOrderDiscountType from './SalesOrderDiscountType';
import AddSalesOrderTable from './AddSalesOrderTable';
import SalesOrderDeliveryMethod from './SalesOrderDeliveryMethod';
import SalesOrderWarehouseName from './SalesOrderWareHouseName';
import GeneralFileUploadPreview from '../../components/GeneralFileUploadPreview';
import CustomerSearchSelect from '../credit_note/CustomerSearchSelect';
import { toLowercaseKeys } from '../../helpers/toLowerCaseKeys';
import SalesOrderAddressModal from './SalesOrderAddressModal';
import { formatFormError } from '../../helpers/formartFormError';

function AddSalesOrderPage() {
  const setEntryData = SetEntryData();
  const entryMasterData = GetEntryData();
  const editableSalesOrder = GetEditSalesOrder();
  const resetEdit = ResetEditSalesOrder();

  const navigate = useNavigate();
  const myPromiseModal = createModal(GeneralModal);
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const logicContext = useContext(LogicContext);
  const [selectedSalesList, setPurchaseList] = useState([]);
  const [selectedInvoiceList, setInvoiceList] = useState([]);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [discountValue, setDiscountValue] = useState(0);
  const [shippingFee, setShippingFee] = useState(0);
  const [adjustmentValue, setAdjustmentValue] = useState(0);
  const [disCountTypeID, setDiscountTypeID] = useState(1);
  const [customer, setCustomer] = useState({});

  // FIX 1: Use ref to track mount state
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef(null);
  const [searchParams] = useSearchParams();
  const customVendorId = searchParams.get('vendor_id');

  console.log('SALE_EDIT ', editableSalesOrder);

  // FIX 2: Proper cleanup for mount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // FIX 3: Fetch data with abort controller
  useEffect(() => {
    if (entryMasterData['Discount_Type'] === undefined) {
      abortControllerRef.current = new AbortController();

      salesService
        .getEntrySalesOrderData(abortControllerRef.current.signal)
        .then(res => {
          if (isMountedRef.current && res?.data !== undefined) {
            setEntryData(res.data);
          }
        })
        .catch(err => {
          if (err.name !== 'AbortError' && isMountedRef.current) {
            console.error('Error fetching entry data:', err);
          }
        });
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [entryMasterData, setEntryData]);

  // Custom vendor ID effect
  useEffect(() => {
    if (customVendorId !== null) {
      setValue('Vendor_Id', customVendorId);
      setValue('Discount_Type_Id', 1);
    }
  }, [customVendorId, setValue]);

  // Edit sales order effect
  useEffect(() => {
    document.title = 'New Sales Order';

    if (editableSalesOrder?.sales_order_ref_no) {
      document.title = 'Modify Sales Order';
      setValue('customer_id', editableSalesOrder?.customer_id);
      setValue('delivery_method_id', editableSalesOrder?.delivery_method_id);
      setValue('customer_notes', editableSalesOrder?.customer_notes);
      setValue('sales_order_ref_no', editableSalesOrder?.sales_order_ref_no);
      setValue('subscription_ref_no', editableSalesOrder?.subscription_ref_no);
      setValue('payment_term_id', editableSalesOrder?.payment_term_id);
      setValue('discount_type_id', editableSalesOrder?.discount_type_id);
      setValue('discount_value', editableSalesOrder?.discount_amount);
      setValue('sales_person_id', editableSalesOrder?.sales_person_id);
      setValue('warehouse_id', editableSalesOrder?.warehouse_id);
      setValue('attachment_url', editableSalesOrder?.sales_order_attachement_url);
      setValue('order_remark', editableSalesOrder?.order_remark);

      setDiscountValue(editableSalesOrder?.discount_amount || 0);
      setShippingFee(editableSalesOrder?.shipping_amount || 0);
      setAdjustmentValue(editableSalesOrder?.adjustment_amount || 0);
      setDiscountTypeID(editableSalesOrder?.discount_type_id);
      setCustomer({
        email_address: editableSalesOrder?.customer_email_address,
        name: editableSalesOrder?.customer_full_name,
        customer_id: editableSalesOrder?.customer_id,
      });
    }

    return () => {
      resetEdit();
      // FIX 4: Clear large state on unmount
      setPurchaseList([]);
      setInvoiceList([]);
      setAttachmentFile(null);
    };
  }, [editableSalesOrder, setValue, resetEdit]);

  const onDeliveryMethodSelect = val => {
    setValue('delivery_method_id', val.Delivery_Method_Id);
  };

  const onWareHouseSelect = val => {
    setValue('warehouse_id', val.Warehouse_Id);
  };

  const onSubmit = async () => {
    const salesForm = getValues();

    if (!salesForm?.customer_id) {
      return logicContext.showToast('Please select a customer', 'info');
    }

    if (selectedSalesList?.length === 0) {
      return logicContext.showToast('at least one or more item must be selected!', 'error');
    }

    // FIX 5: Build XML more efficiently
    const tempStringList = selectedSalesList
      .map(
        item =>
          `<a><item_id>${item.item_id}</item_id>` +
          `<item_quantity>${item?.item_sales_quantity}</item_quantity>` +
          `<item_rate>${item?.item_sales_rate}</item_rate>` +
          `<tax_id>${item?.item_tax_id || 0}</tax_id>` +
          `<account_id>${item.account_id || 1}</account_id></a>`
      )
      .join('');

    const tempInvoiceList = selectedInvoiceList
      .map(invoice => `<a><invoice_ref_no>${invoice.invoice_ref_no}</invoice_ref_no></a>`)
      .join('');

    try {
      const result = await myPromiseModal({
        title: 'Confirmation',
        body: 'Do you want to save this record?',
        showInput: false,
        placeHolder: '',
      });

      if (result.decision === true) {
        let option = editableSalesOrder?.sales_order_ref_no ? 3 : 2;
        const newData = getValues();

        const formData = new FormData();
        Object.keys(newData).forEach(key => {
          formData.set(key, newData[key] || 0);
        });

        formData.set('option', option);
        formData.set('xml_string_item', `<root>${tempStringList}</root>`);
        formData.set('xml_string_invoice', `<root>${tempInvoiceList}</root>`);
        formData.set('discount_value', discountValue || 0);
        formData.set('shipping_charge', shippingFee || 0);
        formData.set('adjustment_amount', adjustmentValue || 0);

        if (attachmentFile) {
          formData.set('file', attachmentFile);
        }

        const res = await salesService.submitSalesOrder(formData);

        if (!isMountedRef.current) return; // FIX 6: Check if still mounted

        if (res?.data !== undefined) {
          const cleanResponse = logicContext.httpResponseManager(res.data?.[0] || res.data?.result_status?.[0]);
          const { message_id, message_text } = cleanResponse;

          if (message_id == 1) {
            logicContext.showToast(message_text, 'success');
            navigate(-1);
            sessionStorage.setItem('reloadPage', 'true');
          }
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        logicContext.showToast('Error submitting form', 'error');
      }
    }
  };

   function onErrors(errors) {
     console.error(errors);
     const errorMessages = formatFormError(errors);
     logicContext.showToast(errorMessages, 'error');
   }

  const onFileSelected = file => {
    setAttachmentFile(file);
  };

  const pickupDate = () => {
    const defaultDate = editableSalesOrder?.pickup_date
      ? moment(editableSalesOrder.pickup_date).format('YYYY-MM-DD')
      : moment(Date.now()).format('YYYY-MM-DD');

    return (
      <div>
        <input
          defaultValue={defaultDate}
          key={defaultDate}
          className="-mt-1 h-11 bg-white text-sm rounded-md border border-gray-200 w-full outline-none"
          type="date"
          {...register('pickup_date', {
            required: 'pick up date is required',
          })}
        />
        <small className="text-red-500">{errors?.pickup_date?.message}</small>
      </div>
    );
  };

  const salesOrderDate = () => {
    const defaultDate = editableSalesOrder?.sales_order_date
      ? moment(editableSalesOrder.sales_order_date).format('YYYY-MM-DD')
      : moment(Date.now()).format('YYYY-MM-DD');

    return (
      <div>
        <input
          defaultValue={defaultDate}
          key={defaultDate}
          className="-mt-1 h-11 bg-white text-sm rounded-md border border-gray-200 w-full outline-none"
          type="date"
          {...register('sales_order_date', {
            required: 'sales order date is required',
          })}
        />
        <small className="text-red-500">{errors?.sales_order_date?.message}</small>
      </div>
    );
  };

  const onSelectionList = tableList => {
    setPurchaseList(tableList);
  };

  // FIX 7: Memoize calculations to prevent unnecessary recalculations
  const calculateSubTotal = list => {
    return list.reduce((total, element) => {
      const quantity = Number(element?.item_sales_quantity) || 0;
      const rate = Number(element?.item_sales_rate) || 0;
      return total + quantity * rate;
    }, 0);
  };

  const onDiscountTypeIDSelect = typeId => {
    setValue('discount_type_id', typeId);
    setDiscountTypeID(Number(typeId));
  };

  const calculateGrandTotal = list => {
    let result = calculateSubTotal(list);

    if (disCountTypeID === 2) {
      result -= (discountValue / 100) * result;
    } else if (disCountTypeID === 1) {
      result -= discountValue;
    }

    result += shippingFee || 0;
    result += adjustmentValue || 0;

    return Math.max(result, 0);
  };

  const onCustomerSelected = customer => {
    if (editableSalesOrder?.sales_order_ref_no) {
      return logicContext.showToast('Changing customer for this sales order is not allowed!', 'error');
    }
    const customerData = toLowercaseKeys(customer);
    setValue('customer_id', customerData?.customer_id);
    setCustomer(customerData);
  };


  return (
    <>
      <h1 className="text-2xl font-bold text-gray-700">
      
        {editableSalesOrder?.sales_order_ref_no ? `Sales Order-#${editableSalesOrder.sales_order_ref_no}` : 'New Sales Order'}
      </h1>
      <div className="mt-6">
        <form
          onSubmit={e => {
            e.stopPropagation();
            handleSubmit(onSubmit, onErrors)(e);
          }}
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">
                Select Customer
                <span className="text-red-500 font-bold">*</span>
              </label>
              {!editableSalesOrder?.sales_order_ref_no && (
                <div className="w-full">
                  <CustomerSearchSelect
                    {...register('customer_id', { required: 'Customer is required' })}
                    onCustomerSelected={onCustomerSelected}
                    className="w-full h-10 bg-white border border-gray-300 rounded-md"
                  />
                  {errors.customer_id?.message && <small className="text-red-500 text-xs">{errors.customer_id.message}</small>}
                </div>
              )}

              {customer?.customer_id && (
                <div className="mt-4 bg-gray-50 rounded-lg p-4">
                  <ul className="space-y-2">
                    <li className="flex items-center bg-blue-50 p-2 rounded-md">
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                      <span className="text-sm">Email: {customer.email_address}</span>
                    </li>
                    <li className="flex items-center bg-blue-50 p-2 rounded-md">
                      <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                      <span className="text-sm">Name: {customer.name}</span>
                    </li>
                  </ul>
                 
                </div>
              )}
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">Sales Order Date</label>
              {salesOrderDate()}
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">Sales Order Number</label>
              <input
                readOnly
                value={editableSalesOrder?.sales_order_no || ''}
                className="-mt-1 h-11 bg-white text-sm rounded-md border border-gray-200 w-full"
                placeholder="Auto generated"
              />
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">Reference Number</label>
              <input
                readOnly
                value={editableSalesOrder?.sales_order_ref_no || ''}
                className="-mt-1 h-11 bg-white text-sm rounded-md border border-gray-200 w-full"
                placeholder="Auto generated"
              />
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">
                Delivery Method<span className="text-red-500">*</span>
              </label>
              <SalesOrderDeliveryMethod
                {...register('delivery_method_id', { required: 'Delivery method required' })}
                defaultVal={{ label: editableSalesOrder?.delivery_method_name }}
                onDeliveryMethodSelect={onDeliveryMethodSelect}
              />
              <small className="text-red-500">{errors?.delivery_method_id?.message}</small>
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">Pickup Date</label>
              {pickupDate()}
            </div>

            <div className="mt-2">
              <label className="ml-1 text-sm text-gray-700">Warehouse</label>
              <SalesOrderWarehouseName defaultVal={{ label: editableSalesOrder?.warehouse_name }} onWareHouseSelect={onWareHouseSelect} />
            </div>
          </div>

          <div className="mt-10">
            <AddSalesOrderTable defaultList={editableSalesOrder?.sales_order_details} onSelectionList={onSelectionList} />

            <div className="mt-8 px-8 py-6 bg-white border rounded-2xl">
              <div className="flex items-end justify-between">
                <div className="text-base font-semibold">Sub Total</div>
                <div className="text-blue-600 text-xl font-semibold">
                  <CurrencyFormat value={calculateSubTotal(selectedSalesList).toFixed(2)} displayType="text" thousandSeparator prefix="₦" />
                </div>
              </div>

              <div className="mt-3 flex items-end justify-between">
                <div className="w-full max-w-[260px] mr-2">
                  <label className="text-sm font-semibold">Discount</label>
                  <div className="relative w-full">
                    <input
                      className="bg-white rounded pl-3 h-11 border border-gray-200 w-full"
                      type="number"
                      value={discountValue}
                      onChange={e => setDiscountValue(Number(e.target.value) || 0)}
                    />
                    <div className="absolute border-l top-0 bottom-0 right-0 rounded-r">
                      <SalesOrderDiscountType
                        defaultVal={editableSalesOrder?.discount_type_id}
                        onDiscountTypeSelect={onDiscountTypeIDSelect}
                      />
                    </div>
                  </div>
                </div>
                <div className="text-blue-600 text-xl font-semibold">
                  <CurrencyFormat value={discountValue.toFixed(2)} displayType="text" thousandSeparator />
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between">
                <div className="text-xl font-bold">Total (₦)</div>
                <div className="text-xl rounded-md font-bold px-6 py-2 bg-gray-100 text-white">
                  <CurrencyFormat
                    value={calculateGrandTotal(selectedSalesList).toFixed(2)}
                    displayType="text"
                    thousandSeparator
                    prefix="₦"
                  />
                </div>
              </div>
            </div>

            <div className="mt-10 grid gap-6">
              <div>
                <label className="ml-1 text-sm text-gray-700">Customer Notes</label>
                <textarea rows={4} {...register('Customer_Notes')} className="bg-white text-sm rounded-md border border-gray-200 w-full" />
              </div>
            </div>

            <div className="mt-8">
              <label className="ml-1 text-sm text-gray-700">Attach File</label>
              <GeneralFileUploadPreview defaultFileUrl={editableSalesOrder?.sales_order_attachement_url} onFileSelected={onFileSelected} />
              <i className="mt-2 text-sm">maximum file size 2MB</i>
            </div>

            <div className="flex justify-between my-10 gap-4">
              <button type="button" className="text-sm text-red-500 bg-white rounded px-4 py-2" onClick={() => navigate(-1)}>
                Cancel
              </button>

              <button type="submit" className="text-sm bg-blue-600 text-white px-6 rounded py-2">
                {editableSalesOrder?.sales_order_ref_no ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}

export default AddSalesOrderPage;
